import { useEffect, useState } from 'react';
import { Container, Row, Col, Table, Button, Modal, Form, Card, Image, Dropdown } from 'react-bootstrap';
import { Header } from '../../features/mobile_header/Header';
import * as Icon from 'react-bootstrap-icons';
import { useDispatch, useSelector } from 'react-redux';
import { selectDevice } from '../../features/devices/deviceSlice';
import { deviceListThunk, regenerateClaimCodeThunk } from '../../features/devices/deviceThunks';
import { useNavigate } from "react-router-dom";
import useDidMountHook from '../../hooks/useDidMountHook';
import { useAuth } from '../../providers/AuthProvider';
import { toast } from 'react-toastify';
import { getPairingState, getLivenessState, relativeTime } from '../../utils/deviceStatus';

const  Devices = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [user] = useAuth();
	const { status, data, claim, claimStatus, claimMessage } = useSelector(selectDevice)
	const [showClaimFor, setShowClaimFor] = useState(null);
	const [claimCopied, setClaimCopied] = useState(false);

	useDidMountHook(() => {
		dispatch(deviceListThunk({ user_id: user?.id }))
	})

	useEffect(() => {
		if (claimStatus === 'error') {
			toast.error(claimMessage || 'Could not generate pairing code');
			setShowClaimFor(null);
		}
	}, [claimStatus, claimMessage]);

	const requestClaimCode = (device, e) => {
		e?.stopPropagation();
		setShowClaimFor(device.device_id);
		setClaimCopied(false);
		dispatch(regenerateClaimCodeThunk({ device_id: device.device_id }));
	}

	const closeClaimModal = () => {
		setShowClaimFor(null);
		setClaimCopied(false);
	}

	const copyClaim = async () => {
		if (!claim?.code) return;
		try {
			await navigator.clipboard.writeText(claim.code);
			setClaimCopied(true);
			setTimeout(() => setClaimCopied(false), 2000);
		} catch (e) {
			toast.info('Copy unavailable — write the code down manually.');
		}
	}

	const formatExpiry = (iso) => {
		if (!iso) return '';
		try {
			const minutes = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
			return `Expires in ${minutes} min`;
		} catch (e) { return ''; }
	}

	return (
		<>
			<Header page={'devices'} />
			<Container fluid className="element-page main h-100 pt-5 pb-5">
				<Row className="h-100">
					<Col lg={12}>
						<Row className="content align-items-center">
							<Col lg={10} md={12}><h1>Your Devices</h1></Col>
							<Col lg={2} sm={6} className='pb-1 text-right'>
								<Button className="" variant="primary" onClick={() => navigate('/add-device')}>Add device</Button>
							</Col>
						</Row>

						<Row className="content align-items-stretch">
							 
							{(() => {
								if (status === "loaded") {
									var devices_list = data;

									if (devices_list.length > 0) {
										return (
											<>
												{devices_list.map((device, i) => (
													<Col lg={4} key={i} className='mb-3'>
														<Card className='mx-2' onClick={() => navigate(`/update-device/${device.device_id}`)}>
															<Card.Body>
																<Card.Title>{device.name}</Card.Title>
																<Card.Text className={(device?.paymentStatus === 'Cancelled' ? 'cancel' : (device.isPaidSubscription ? 'active' : 'free')) + ' status hidden'}>{device?.paymentStatus === 'Cancelled' ? 'Cancelled' : (device.isPaidSubscription ? 'Paid' : 'Free')}</Card.Text>
																<Card.Text>{device.device_id}</Card.Text>
																{(() => {
																	const pair = getPairingState(device);
																	const live = getLivenessState(device);
																	return (
																		<div className='d-flex flex-wrap gap-2 mt-2' style={{ gap: 6 }}>
																			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 12, background: 'rgba(255,255,255,0.06)', fontSize: 12, color: pair.color }}>
																				<span style={{ width: 8, height: 8, borderRadius: '50%', background: pair.color }} />
																				{pair.label}
																			</span>
																			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 12, background: 'rgba(255,255,255,0.06)', fontSize: 12, color: live.color }}>
																				<span style={{ width: 8, height: 8, borderRadius: '50%', background: live.color }} />
																				{live.label}
																				{live.lastSeenAt && live.kind !== 'online' && (
																					<span style={{ marginLeft: 4, color: '#9CA3AF' }}>· {relativeTime(live.lastSeenAt)}</span>
																				)}
																			</span>
																		</div>
																	);
																})()}
																<Card.Text className='mt-5 crypto-logo'>
																	{(device?.cryptoCurrency ?? []).map((crypto, j) => (
																		<span key={j} className='cryptox avatar'>{crypto}</span>
																	))}
																</Card.Text>
																<Button
																	size="sm"
																	variant="outline-light"
																	className="mt-2"
																	onClick={(e) => requestClaimCode(device, e)}
																>
																	Get pairing code
																</Button>
															</Card.Body>
														</Card>
													</Col>
												))}
											</>

										)
									} else {
										return (
											<>
												<Col className='col-12 text-center'>No device yet</Col>
											</>
										)
									}
								}
							

								if (status === "loading") {
									return (
										<>
											<Col lg={12}>Loading...</Col>
										</>
									)
								}
							})()}

							<Col lg={4} >
								<Card className='mb-5 mx-2 add-new d-none' style={{
									padding: "3rem 0px"
								}} onClick={() => navigate('/add-device')}>
									<Card.Body>
										<Icon.Plus /> Add Device
									</Card.Body>
								</Card>
							</Col>
						</Row>
					</Col>
				</Row>
			</Container>

			<Modal show={!!showClaimFor} onHide={closeClaimModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Pair Your Device</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						Open the <strong>CryptoX Manager</strong> mobile app, connect to your device,
						and enter this claim code on the WiFi setup screen.
					</p>
					<div className="text-center my-4 p-4" style={{
						background: '#1F1F35',
						borderRadius: '12px',
						border: '2px solid #7C3AED',
					}}>
						{claimStatus === 'loading' ? (
							<div style={{ color: '#9CA3AF' }}>Generating…</div>
						) : claim?.code ? (
							<>
								<div style={{
									fontFamily: 'monospace',
									fontSize: '2.5rem',
									letterSpacing: '0.5rem',
									fontWeight: 'bold',
									color: '#fff',
								}}>
									{claim.code}
								</div>
								<div className="mt-2" style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
									{formatExpiry(claim.expiresAt)}
								</div>
							</>
						) : (
							<div style={{ color: '#EF4444' }}>Could not generate code</div>
						)}
					</div>
					<p style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
						Codes are one-time use and expire in 15 minutes. Generate a new one any time.
					</p>
				</Modal.Body>
				<Modal.Footer>
					{claim?.code && (
						<Button variant="secondary" onClick={copyClaim}>
							{claimCopied ? 'Copied!' : 'Copy code'}
						</Button>
					)}
					<Button variant="primary" onClick={closeClaimModal}>Done</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}


export default Devices
